import { useEffect, useState, type FormEvent } from 'react';
import { CloudError, login, resendConfirmationEmail, type CloudSession } from '../lib/cloud';

type ConfirmationResult = 'returned' | 'expired' | 'invalid';
declare global { interface Window { __APOLLO_EMAIL_CONFIRMATION__?: ConfirmationResult } }

export function emailConfirmationStatus(): ConfirmationResult | null {
  const value = window.__APOLLO_EMAIL_CONFIRMATION__;
  return value === 'returned' || value === 'expired' || value === 'invalid' ? value : null;
}

const confirmationMessages: Record<ConfirmationResult, string> = {
  returned: 'Your confirmation link has returned to Apollo. Sign in with your password to check your email verification.',
  expired: 'This confirmation link has expired. Request another email below, then sign in.',
  invalid: 'This confirmation link could not be verified. Request another email below or try signing in again.',
};

export function CloudSignIn({ onSignedIn }: { onSignedIn: (session: CloudSession) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'login' | 'resend' | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(() => { const status = emailConfirmationStatus(); return status ? confirmationMessages[status] : ''; });
  const [waitSeconds, setWaitSeconds] = useState(0);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setTimeout(() => setWaitSeconds(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy('login'); setError(''); setNotice('');
    try { onSignedIn(await login(email.trim(), password)); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Sign in failed. Try again.'); }
    finally { setPassword(''); setBusy(null); }
  }

  async function resend() {
    if (busy || waitSeconds > 0) return;
    setBusy('resend'); setError(''); setNotice('');
    try {
      await resendConfirmationEmail(email);
      setNotice('If this account is awaiting email confirmation, a new confirmation email has been requested. Check your inbox and spam folder, follow the link, then sign in with your password.');
      setWaitSeconds(60);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The confirmation email could not be requested. Try again later.');
      if (failure instanceof CloudError && failure.status === 429) setWaitSeconds(60);
    } finally { setBusy(null); }
  }

  return <form className="cloud-form" onSubmit={signIn}>
    {notice && <div className="plain-notice" role="status">{notice}</div>}
    <label>Email<input type="email" autoComplete="username" required disabled={!!busy} value={email} onChange={event => setEmail(event.target.value)} /></label>
    <label>Password<input type="password" autoComplete="current-password" required disabled={!!busy} value={password} onChange={event => setPassword(event.target.value)} /></label>
    <button className="primary-button" disabled={!!busy}>{busy === 'login' ? 'Signing in…' : 'Sign in to Supabase'}</button>
    {error && <p className="form-error" role="alert">{error}</p>}
    <p className="small-muted">Need to confirm your email? Request a confirmation for your existing account. This does not create a new account.</p>
    <button className="secondary-button" type="button" disabled={!!busy || waitSeconds > 0 || !email.trim()} onClick={resend}>{busy === 'resend' ? 'Requesting confirmation…' : waitSeconds > 0 ? `Resend available in ${waitSeconds}s` : 'Resend confirmation email'}</button>
  </form>;
}
